import AmpService, { AMP_CATEGORY_LOCATIONS } from "analytics/amplitude/amplitudeEvents";
import { ICompetitorPages } from "api/interfaces/static-pages/competitor-pages.type";
import PAButton, { BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import Slider from "component/design-system/carousel/Slider";
import PAImage from "component/design-system/images/PAImage";
import Element from "component/design-system/react-scroll/components/Element";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import PageContainer from "modules/components/PageContainer";
import SafeLink from "modules/components/SafeLink";
import TabsCategory from "modules/home/TabsCategory";
import ContentSection from "modules/home/car-parts/ContentSection";
import { tabIndexMap } from "modules/home/tabIndex";
import Testimonials from "modules/home/testimonials/Testimonials";
import MetaHead from "modules/site-structure/common/MetaHead";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { renderHTML } from "util/DomHelper";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { scrollTo } from "util/scroll-helper";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import ContentIdsComponent from "../content-ids/contentIds";
import Banner from "./Banner";
import CompareVideoSection from "./CompareVideoSection";
import Table from "./Table";
import VideoWithThumbnail from "./VideoWithThumbnail";
import commonStyles from "./commonStyles.module.scss";
import styles from "./competitor-page.module.scss";
const DepartmentMenu = dynamic(() => import(/* webpackChunkName: "department-menu" */ "component/department-menu/DepartmentMenu"));

function CompetitorPage(props: ICompetitorPages) {
  const { staticData } = props;

  const {
    topCategories,
    highTrafficPageData,
    googleReviews,
  } = staticData;

  const {
    cmsImageUrls, metaDescription, metaKeywords, metaTitle,
    buttons = [], tables = [], headers, reviews, All_Categories, contentIds,
    videos, videosHeading, bannerImages, comparisionVideo, comparisionVideoData, affiliatedText = "",
  } = highTrafficPageData;

  const defaultTab = topCategories?.length > 0 ? 1 : 2;
  const [categoryActiveTab, setCategoryActiveTab] = useState(defaultTab);
  const { href, asPath } = getHrefAndPath({ href: "/replacement-parts", asPath: "/replacement-parts" });
  const [isMobile, isTablet] = useScreenSize();
  const { t } = useTranslation("home");

  const { query } = useRouter();
  const { pathname } = query;

  const { garage } = useSelector((state: RootState) => state);
  const { selectedVehicle } = garage;

  useEffect(() => {
    if (validateVehicle(selectedVehicle)) {
      scrollTo("anchor-tab-category");
    }
  }, [selectedVehicle]);

  return (
    <PageContainer fullWidth className={styles.page_container}>
      <Head>
        <link rel="preload" as="image" href={getRelativeImagePath(`/cms/${cmsImageUrls[0]}`)} />
      </Head>
      <MetaHead {...{
        keywordsV2: metaKeywords,
        description: metaDescription,
        title: metaTitle,
        contentIds,
        pathname,
        videos: [...videos, comparisionVideo].filter(Boolean),
      }}
      />
      <div>
        <div className="d-flex align-items-center justify-content-center">
          <div className={`${styles.h1_container} ${styles.container}`}>
            <PAText component="h1" className="m-t-0" bold color="black">
              {headers.h1}
            </PAText>
            <div className="m-t-10 m-b-10">
              <Banner bannerImages={bannerImages} />
            </div>
            <div className="d-flex">
              <div className={styles.left_section}>
                <PAText fontSize={FONT_SIZE[5]} component="p" color="black" dangerouslySetInnerHTML={renderHTML(headers.description)} />
                <HideIf show={headers.competitorRatingImage && headers.PARatingImage}>
                  <div className={styles.rating_image_container}>
                    <SafeLink href={headers.PARatingUrl} target="_blank">
                      <PAImage alt="PA Rating Image" title="PA Rating Image" src={getRelativeImagePath(`/cms/${headers.PARatingImage}`)} desktopHeight="calc(7 * var(--safari-root-font, 10px))" tabletHeight="calc(5 * var(--safari-root-font, 10px))" mobileHeight="calc(5 * var(--safari-root-font, 10px))" />
                    </SafeLink>
                    <SafeLink href={headers.competitorRatingURL} target="_blank">
                      <PAImage alt={`${pathname} Rating Image`} title={`${pathname} Rating Image`} src={getRelativeImagePath(`/cms/${headers.competitorRatingImage}`)} desktopHeight="calc(7 * var(--safari-root-font, 10px))" tabletHeight="calc(5 * var(--safari-root-font, 10px))" mobileHeight="calc(5 * var(--safari-root-font, 10px))" />
                    </SafeLink>
                  </div>
                </HideIf>
              </div>
              <HideIf show={!isMobile && !isTablet}>
                <div className={styles.right_section}>
                  <Image unoptimized layout="responsive" width={450} height={350} src={getRelativeImagePath(`/cms/${headers.rightImage}`)} />
                </div>
              </HideIf>
            </div>

            <div className="d-flex align-items-center justify-content-center m-t-20">
              <SafeLink href={href} asPath={asPath}>
                <PAButton buttonType={BUTTON_TYPE.SECONDRY} className={commonStyles.button_style}>
                  {buttons[0]?.name}
                </PAButton>
              </SafeLink>
            </div>
          </div>
        </div>
        <HideIf show={tables.length}>
          <div className={`${styles.center_content_container} ${styles.container}`}>
            <Table table={tables?.[0]} buttonTitle={buttons[1]?.name}>
              <PAText fontSize={FONT_SIZE[0]} color="light-grey" className="d-flex justify-content-end">
                {`*${t("Shipping to")} M5J 2L9, Toronto, Ontario, Canada`}
              </PAText>
            </Table>
          </div>
        </HideIf>
        <HideIf show={comparisionVideo?.youtubeId}>
          <CompareVideoSection
            title={comparisionVideo?.title}
            youtubeId={comparisionVideo?.youtubeId}
            heading={comparisionVideoData?.heading}
            points={comparisionVideoData?.points}
            href={href}
            asPath={asPath}
            button={comparisionVideoData?.button}
          />
        </HideIf>

        <div className={`${styles.categories_section} ${styles.gray_background} `}>
          <ContentSection
            backgroundColor="transparent"
            heading={All_Categories.heading}
            content={All_Categories.description}
          />
          <HideIf show={!isMobile}>
            <Element name="anchor-tab-category" />
            <div className="m-t-30">
              <TabsCategory
                topCategories={topCategories}
                currentTab={categoryActiveTab}
                onChange={(index: number) => {
                  if (tabIndexMap[index]) {
                    AmpService.categoryClick({ tier0: tabIndexMap[index] }, AMP_CATEGORY_LOCATIONS.MAIN_CATEGORY_GRID);
                  }
                  setCategoryActiveTab(index);
                }}
                component="h3"
              />
            </div>
          </HideIf>
          <HideIf show={isMobile}>
            <DepartmentMenu />
          </HideIf>

        </div>

        {/* <div className={`${styles.gray_background} ${styles.container}`}>
          <PAText component="h2" fontSize={FONT_SIZE[5]} centerAlign bold color="black" className={`m-t-0 m-b-20 ${styles.font_size_big} `}>
            {featuresData.heading}
          </PAText>
          <HideIf show={featuresData?.data && featuresData?.data?.length > 0}>
            <Slider
              tileWidth={300}
              liClass={styles.li_class}
              showScrollOnHover
              itemStyle={{ backgroundColor: "transparent", border: "none" }}
            >
              {featuresData?.data?.map((data) => (
                <div className={styles.features_data} key={data.title}>
                  <PAImage height={60} src={getRelativeImagePath(`/cms/${data.src}`)} />
                  <PAText centerAlign component="h3" color="pa-blue" className={`m-b-0 m-t-5 ${styles.font_size_medium} `}>
                    <Content content={data.title} />
                  </PAText>
                </div>
              ))}
            </Slider>
          </HideIf>

          <div className="d-flex align-items-center justify-content-center m-t-20">
            <SafeLink href={href} asPath={asPath}>
                <PAButton buttonType={BUTTON_TYPE.SECONDRY} className={commonStyles.button_style}>
                  {buttons[3]?.name}
                </PAButton>
            </SafeLink>
          </div>
        </div> */}

        <HideIf show={tables.length}>
          <div className={`${styles.center_content_container} ${styles.container}`}>
            <Table table={tables[1]} buttonTitle={buttons[2].name} rowBorder />
          </div>
        </HideIf>

        <div className={`${styles.gray_background} p-20`}>
          <PAText
            component="h2"
            style={{ width: "100%" }}
            fontSize={FONT_SIZE[5]}
            bold
            color="black"
            className={`d-flex align-items-center justify-content-center m-b-20 m-t-0 ${styles.font_size_big} `}
          >
            {videosHeading}
          </PAText>
          <Slider
            tileWidth={isMobile ? 280 : 400}
            liClass={styles.li_class}
            showScrollOnHover
            itemStyle={{ backgroundColor: "transparent", border: "none" }}
          >
            {videos.map((video) => (

              <VideoWithThumbnail key={video.youtubeId} video={video} className={styles.video_container} />

            ))}
          </Slider>

          <HideIf show={buttons[4]?.name}>
            <div className="d-flex align-items-center justify-content-center m-t-20">
              <SafeLink href={href} asPath={asPath}>
                <PAButton buttonType={BUTTON_TYPE.SECONDRY} className={commonStyles.button_style}>
                  {buttons[4]?.name}
                </PAButton>
              </SafeLink>
            </div>
          </HideIf>

        </div>

        <div className={`${styles.review_container}`}>
          <div className="d-flex" style={{ position: "relative" }}>
            <PAText
              component="h2"
              className={`d-flex align-items-center justify-content-center m-b-15 ${styles.font_size_big} `}
              style={{ width: "100%" }}
              fontSize={FONT_SIZE[5]}
              bold
              color="black"
              centerAlign
            >
              {reviews.heading}
            </PAText>

          </div>
          <Testimonials
            reviews={googleReviews}
            tileWidth={isMobile ? 300 : 500}
            className={styles.testimonials}
          />
          <div className="d-flex align-items-center justify-content-center m-t-30">
            <SafeLink href={href} asPath={asPath}>
              <PAButton buttonType={BUTTON_TYPE.SECONDRY} className={`${commonStyles.button_style} m-b-20`}>
                {buttons[5]?.name}
              </PAButton>
            </SafeLink>
          </div>
        </div>

        <div className={`${styles.center_content_container} ${styles.container}`}>
          <div className={styles.faq_container}>

            <PAText component="h2" fontSize={FONT_SIZE[5]} centerAlign bold color="black">
              {t("FREQUENTLY ASKED QUESTION")}
            </PAText>
            <PAText component="h3" color="black" centerAlign fontSize={FONT_SIZE[5]}>
              {t("Question you might ask about product and services")}
            </PAText>
            <ContentIdsComponent contentIds={contentIds} classNames={styles.content_id} yellowHeading={false} />

          </div>
        </div>
        <PAText fontSize={FONT_SIZE[2]}>
          {t("doubt_avoidance", { text: affiliatedText || metaTitle })}
        </PAText>

      </div>
    </PageContainer>
  );
}

export default CompetitorPage;
